import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import { useCreditStore } from '@stores/creditStore';
import { usePlanApprovalStore } from '@stores/planApprovalStore';
import { usePlatformTrial } from '@hooks/usePlatformTrial';
import { PLANS_PAGE_PATHS } from '@lib/services/credit/platformTrial';
import { ApprovePlanModal } from './ApprovePlanModal';

const VISIBILITY_REFETCH_THROTTLE_MS = 60_000;
// setTimeout's delay is a signed 32-bit int; anything larger fires immediately.
const MAX_TIMEOUT_MS = 2_147_483_647;
// While read-only, poll for the webhook that would lift the lock (the merchant
// approved in Shopify but the backend hasn't processed the webhook yet): every
// 15s for the first 2 minutes after readOnly became true on this mount.
const READONLY_POLL_INTERVAL_MS = 15_000;
const READONLY_POLL_MAX_COUNT = 8; // 8 × 15s = 2 minutes

function promptedKey(userId: string): string {
  return `yepai_trial_approval_prompted_${userId}`;
}

/**
 * No UI of its own besides mounting the one `ApprovePlanModal` instance.
 * Mounted once, app-wide, by `DashboardLayout` (PRD V6.0 ①/③):
 *
 * - Auto-opens the approval dialog the first time a read-only merchant lands
 *   on any page other than the in-app plans page, at most once ever per
 *   browser/user (a localStorage flag) — not once per day; the permanent
 *   `ReadOnlyBar` covers every later visit.
 * - Keeps `overview` fresh while the trial clock is running but the merchant
 *   has not yet been locked, so the switch to read-only/the dialog's own
 *   copy doesn't wait on an unrelated page remounting `fetchOverview`.
 */
export function PlatformTrialController() {
  const { awaitingApproval, readOnly, trialEndsAt } = usePlatformTrial();
  const user = useAuthStore(s => s.user);
  const open = usePlanApprovalStore(s => s.open);
  const location = useLocation();
  const userId = user?.id;

  // Guards the storage-throws fallback: still at most once per MOUNT, even if
  // every render re-evaluates the effect (e.g. a path change) and storage
  // never succeeds in recording that we already prompted.
  const openedThisMountRef = useRef(false);

  useEffect(() => {
    if (!readOnly || !userId) return;

    const onPlansPage = PLANS_PAGE_PATHS.some(path => location.pathname.startsWith(path));
    if (onPlansPage) return;

    const key = promptedKey(userId);
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
      open();
    } catch {
      if (openedThisMountRef.current) return;
      openedThisMountRef.current = true;
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, userId, location.pathname]);

  // Scheduled one-shot refetches at trialEndsAt + 2s and trialEndsAt + 30s,
  // while the trial clock is still running and the merchant is not yet
  // locked. Two, not one: the client clock may run ahead of the server, so a
  // merchant whose browser clock is a bit fast would otherwise refetch before
  // the backend has actually flipped them to read-only.
  useEffect(() => {
    if (!awaitingApproval || readOnly || !trialEndsAt || !userId) return;
    const end = new Date(trialEndsAt).getTime();
    if (Number.isNaN(end) || end <= Date.now()) return;

    const refetch = () => useCreditStore.getState().fetchOverview(userId);
    const timerAt2s = window.setTimeout(refetch, Math.min(end + 2000 - Date.now(), MAX_TIMEOUT_MS));
    const timerAt30s = window.setTimeout(
      refetch,
      Math.min(end + 30_000 - Date.now(), MAX_TIMEOUT_MS)
    );
    return () => {
      window.clearTimeout(timerAt2s);
      window.clearTimeout(timerAt30s);
    };
  }, [awaitingApproval, readOnly, trialEndsAt, userId]);

  // Poll while read-only, in case the merchant approved in Shopify and
  // returned before the webhook landed: every 15s for the first 2 minutes
  // after readOnly became true on THIS mount (the effect re-runs, and so
  // restarts the window, whenever readOnly or userId changes).
  useEffect(() => {
    if (!readOnly || !userId) return;

    let count = 0;
    const interval = window.setInterval(() => {
      count += 1;
      useCreditStore.getState().fetchOverview(userId);
      if (count >= READONLY_POLL_MAX_COUNT) {
        window.clearInterval(interval);
      }
    }, READONLY_POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [readOnly, userId]);

  // Refetch on the tab becoming visible again, throttled to at most once/60s.
  const lastVisibilityFetchRef = useRef(0);
  useEffect(() => {
    if (!awaitingApproval || !userId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityFetchRef.current < VISIBILITY_REFETCH_THROTTLE_MS) return;
      lastVisibilityFetchRef.current = now;
      useCreditStore.getState().fetchOverview(userId);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [awaitingApproval, userId]);

  return <ApprovePlanModal />;
}
